import { Badge, Center, HStack, Table, Text, VStack } from '@chakra-ui/react';
import { TopBannerItemFragment } from '@client/graphql/types/graphql';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context';
import { useTranslate } from '@tolgee/react';
import { capitalize } from 'lodash';
import moment from 'moment-timezone';
import TopBannerUpdateModal from './top-banner-update-modal';

interface TopBannerTableProps {
  data: TopBannerItemFragment[];
}

export default function TopBannerTable({ data }: TopBannerTableProps) {
  const { t } = useTranslate();
  const company = useFocusedCompanyContext();

  if (data.length == 0) {
    return (
      <Center py={3}>
        <Text color={'gray.500'} fontStyle={'italic'}>
          {t('top-banner.alert.empty', 'Top banner data is empty')}
        </Text>
      </Center>
    );
  }

  const formatTime = (time: string) => {
    return moment.tz(time, company.settings.timezone).format('DD/MM/YYYY HH:mm');
  };

  const rows = data.map((item) => (
    <Table.Row key={item.id}>
      {/* Desktop View */}
      <Table.Cell display={{ base: 'none', md: 'table-cell' }} borderBottom="1px solid" borderColor="gray.200">
        <Badge
          color={'white'}
          bgColor={item.isActive ? 'green.500' : 'gray.300'}
          variant="subtle"
          px={2}
          py={1}
          borderRadius="full"
        >
          {item.isActive ? t('common.status.active', 'Active') : t('common.status.inactive', 'Inactive')}
        </Badge>
      </Table.Cell>
      <Table.Cell display={{ base: 'none', md: 'table-cell' }} borderBottom="1px solid" borderColor="gray.200">
        <Text truncate maxW={'48'}>
          {item.title}
        </Text>
      </Table.Cell>
      <Table.Cell display={{ base: 'none', md: 'table-cell' }} borderBottom="1px solid" borderColor="gray.200">
        {item.platforms.map((platform: string) => t(`banner-platform.${platform}`, capitalize(platform))).join(', ')}
      </Table.Cell>
      <Table.Cell display={{ base: 'none', md: 'table-cell' }} borderBottom="1px solid" borderColor="gray.200">
        <HStack gap={2}>
          <Text>{formatTime(item.startTime)}</Text>
          <Text color="gray.500">→</Text>
          <Text>{formatTime(item.endTime)}</Text>
        </HStack>
      </Table.Cell>

      {/* Mobile View */}
      <Table.Cell display={{ base: 'table-cell', md: 'none' }} borderBottom="1px solid" borderColor="gray.200">
        <VStack align="start" gap={2}>
          <HStack>
            <Badge
              color={'white'}
              bgColor={item.isActive ? 'green.500' : 'gray.300'}
              variant="subtle"
              px={2}
              py={1}
              borderRadius="full"
            >
              {item.isActive ? t('common.status.active', 'Active') : t('common.status.inactive', 'Inactive')}
            </Badge>
          </HStack>
          <Text fontWeight={'semibold'} truncate maxW={'48'}>
            {item.title}
          </Text>
          <Text>
            {item.platforms
              .map((platform: string) => t(`banner-platform.${platform}`, capitalize(platform)))
              .join(', ')}
          </Text>
          <VStack align="start" gap={0}>
            <Text fontSize="sm">
              {formatTime(item.startTime)}{' '}
              <Text as="span" color="gray.500">
                →
              </Text>
            </Text>
            <Text fontSize="sm">{formatTime(item.endTime)}</Text>
          </VStack>
        </VStack>
      </Table.Cell>

      {/* Edit button - visible on both views */}
      <Table.Cell borderBottom="1px solid" borderColor="gray.200">
        <TopBannerUpdateModal id={item.id} />
      </Table.Cell>
    </Table.Row>
  ));

  return (
    <>
      <Table.Root size="lg" interactive>
        <Table.Header>
          <Table.Row bgColor={'bg.subtle'}>
            {/* Desktop Headers */}
            <Table.ColumnHeader fontWeight={600} display={{ base: 'none', md: 'table-cell' }}>
              {t('top-banner.table.status', 'Status')}
            </Table.ColumnHeader>
            <Table.ColumnHeader fontWeight={600} display={{ base: 'none', md: 'table-cell' }}>
              {t('top-banner.table.title', 'Title')}
            </Table.ColumnHeader>
            <Table.ColumnHeader fontWeight={600} display={{ base: 'none', md: 'table-cell' }}>
              {t('top-banner.table.platforms', 'Platforms')}
            </Table.ColumnHeader>
            <Table.ColumnHeader fontWeight={600} display={{ base: 'none', md: 'table-cell' }}>
              {t('top-banner.table.time-range', 'Time Range')}
            </Table.ColumnHeader>

            {/* Mobile Headers */}
            <Table.ColumnHeader fontWeight={600} display={{ base: 'table-cell', md: 'none' }}>
              {t('top-banner.table.banner-info', 'Banner Information')}
            </Table.ColumnHeader>

            {/* Edit column - visible on both views */}
            <Table.ColumnHeader fontWeight={600} width="100px">
              {t('common.button.action', 'Action')}
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>

        <Table.Body>{rows}</Table.Body>
      </Table.Root>
    </>
  );
}
